import React, { useState } from "react";
import { Datev, GQL } from "@binale-tech/shared";

import CompanyQueriesGraphql from "./companyQueries.graphql";
import {
    Tooltip,
    ColorPicker,
    Divider,
    Flex,
    Form,
    Image,
    Input,
    message,
    Popconfirm,
    Select,
    Space,
    Spin,
    Drawer,
} from "antd";
import { Button, Kbd, Accordion, AccordionItem } from "@heroui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { LegalFormSelect } from "appearance/components/shared/form/baseComponents";
import { PaymentUtils } from "scripts/models/utils/PaymentUtils";
import { ProductAccessUtils } from "scripts/models/utils/ProductAccessUtils";
import { YearPeriodContext } from "scripts/context/CompanyContext";
import { UserContext, UserControlContext } from "scripts/context/UserProvider";
import { useGQLRetriever } from "scripts/graphql/gqlRetriever";
import { useGqlMutator } from "scripts/graphql/useGqlMutator";
import { validation } from "scripts/infrastructure/helpers/validation";

import "./CompanyFormDrawer.css";
import { DatevContext } from "scripts/context/DatevContext";
import { CheckCircleTwoTone, CloseOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import { datevTokenRefresh, datevTokenRevoke, redirectToDatevOAuth } from "../../../scripts/infrastructure/oauth/oauth";
import { AppRoutes } from "../../../scripts/routing/routeConstants";
import { Link } from "react-router-dom";
import { DatevButton } from "../oauth/DatevButton";
import { useApolloClient } from "@apollo/client";
import { DatevDescriptions } from "../oauth/DatevDescriptions";
import { DatevClients } from "../oauth/DatevClients";
import Dropzone from "react-dropzone";
import { fileToBase64 } from "../../../scripts/converters/converters";
import { PaymentTypeOption } from "@app/views/productSharedComponents/ZahlungenModal/PaymentTypeSelector/PaymentTypeOption";

type Props = {
    onHide?: () => void;
    onComplete?: (v: GQL.ICompany) => void;
    companyId: string;
    isStepperMode?: boolean;
};

const DEFAULT_COLOR = "#F8F8F8";
const colors = [
    "#B80000",
    "#DB3E00",
    "#FCCB00",
    "#008B02",
    "#006B76",
    "#1273DE",
    "#004DCF",
    "#5300EB",
    "#EB9694",
    "#FAD0C3",
    "#FEF3BD",
    "#C1E1C5",
    "#BEDADC",
    "#C4DEF6",
    "#BED3F3",
    "#D4C4FB",
    "#E7E7E7",
    DEFAULT_COLOR,
].reverse();

interface ColorProps {
    value?: string;
    onChange?: (value: string) => void;
}

type FormValues = Pick<
    GQL.ICompany,
    "name" | "color" | "favouritePaymentType" | "datevNrConsultant" | "datevNrCompany" | "type" | "logo"
> &
    Required<Pick<GQL.ICompany, "legalName" | "legalForm">>;

const FormColorPicker: React.FC<ColorProps> = ({ value, onChange }) => {
    return (
        <ColorPicker
            value={value}
            onChange={(color, hex) => onChange(hex)}
            presets={[{ colors, label: "" }]}
            format="hex"
        />
    );
};

export const CompanyFormDrawer: React.FC<Props> = ({ companyId, onHide, onComplete, isStepperMode }) => {
    const intl = useIntl();
    const { fireUser } = React.use(UserContext);
    const { year } = React.use(YearPeriodContext);
    const { fetchUserData } = React.use(UserControlContext);
    const { isLoading: isDatevLoading, datevUserInfo } = React.use(DatevContext);
    const [form] = Form.useForm<FormValues>();
    const formDatevNrConsultant = Form.useWatch("datevNrConsultant", form);
    const formDatevNrCompany = Form.useWatch("datevNrCompany", form);
    const logo = Form.useWatch("logo", form);
    const Mutator = useGqlMutator();
    const client = useApolloClient();
    const retriever = useGQLRetriever<"company">();
    const [open, setOpen] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const [company, setCompany] = React.useState<GQL.ICompany | null>();
    const [datevCompanyUserInfo, setDatevCompanyUserInfo] = useState<GQL.IDatevUserInfo>();
    const hasAccounting = Boolean(company?.id) && ProductAccessUtils.hasCompanyAccounting(company);

    const availableDatevIds = (datevUserInfo?.clients || []).map(c => c.id);
    const isDatevConnectionAvailable = availableDatevIds.some(
        id => company && id === `${formDatevNrConsultant}-${formDatevNrCompany}`
    );
    const isDatevConnected = company?.datevOAuth && Datev.getRefreshTokenValidity(company?.datevOAuth) > 0;

    const getCompanyData = React.useCallback(() => {
        setLoading(true);
        retriever
            .query({ id: companyId, query: CompanyQueriesGraphql.queryCompanyForEdit })
            .then(data => {
                setCompany(data?.company);
                return data?.company;
            })
            .then(companyData => {
                if (!companyData?.datevOAuth) {
                    return Promise.resolve(null);
                }
                return client.query({
                    query: CompanyQueriesGraphql.queryDatevCompanyUserInfo,
                    variables: { companyId },
                    fetchPolicy: "network-only",
                });
            })
            .then(res => setDatevCompanyUserInfo(res?.data?.datevCompanyUserInfo))
            .finally(() => setLoading(false));
    }, [client, companyId, retriever]);

    React.useEffect(() => {
        if (companyId) {
            getCompanyData();
        } else {
            setCompany(null);
            setDatevCompanyUserInfo(null);
        }
    }, [getCompanyData, companyId]);

    React.useEffect(() => {
        form.setFieldsValue(company ? { ...company } : {});
    }, [form, company]);

    const saveCompany = (values: FormValues) => {
        const optionalsInputs: Pick<GQL.ICompany, "datevNrCompany" | "datevNrConsultant"> = {};
        const { datevNrCompany, datevNrConsultant, ...restValues } = values;
        if (datevNrCompany) {
            optionalsInputs.datevNrCompany = Number(datevNrCompany);
        }
        if (datevNrConsultant) {
            optionalsInputs.datevNrConsultant = Number(datevNrConsultant);
        }
        if (!company) {
            return Mutator.mutate<"companyCreate", GQL.ICompanyCreateInput>({
                mutation: CompanyQueriesGraphql.mutationCreateCompany,
                input: {
                    ...restValues,
                    ...optionalsInputs,
                },
            }).then(res => res.companyCreate);
        }
        return Mutator.mutate<"companyUpdate", GQL.ICompanyUpdateInput>({
            mutation: CompanyQueriesGraphql.mutationUpdateCompany,
            input: {
                ...optionalsInputs,
                ...restValues,
                id: company.id,
            },
        }).then(res => res.companyUpdate);
    };

    const handleClose = () => {
        setOpen(false);
        return new Promise(r =>
            setTimeout(() => {
                onHide();
                return r;
            }, 500)
        );
    };

    const handleSubmit = async (values: FormValues) => {
        setLoading(true);
        const gqlCompany = await saveCompany(values);

        await fetchUserData();
        setLoading(false);
        form.resetFields();
        await handleClose();
        onComplete && onComplete(gqlCompany);
    };

    const selectOptions = React.useMemo(() => {
        if (!company) {
            return [];
        }
        return PaymentUtils.getCompanyPaymentMethods(company, year);
    }, [company, year]);

    const handleDatevAuth = async () => {
        setLoading(true);
        await saveCompany(form.getFieldsValue());
        setLoading(false);
        await redirectToDatevOAuth(client, fireUser.uid, company);
    };
    const handleRefresh = async () => {
        client
            .mutate<Pick<GQL.IMutation, "datevTokenRefresh">, GQL.IMutationDatevTokenRefreshArgs>({
                mutation: datevTokenRefresh,
                variables: { input: { companyId, forceRefresh: true } },
            })
            .then(() => getCompanyData())
            .catch(e => message.error(e.message));
    };
    const handleDatevTokenRevocation = async () => {
        client
            .mutate<Pick<GQL.IMutation, "datevTokenRevoke">, GQL.IMutationDatevTokenRevokeArgs>({
                mutation: datevTokenRevoke,
                variables: { companyId },
            })
            .then(res => getCompanyData())
            .catch(e => message.error(e.message));
    };

    return (
        <Drawer
            className="CompanyForm-Modal"
            maskClosable
            destroyOnHidden
            open={open}
            onClose={handleClose}
            width={750}
            title={
                <Flex style={{ width: "100%" }} justify={"space-between"} align={"center"}>
                    <span>
                        <FormattedMessage id="app.titles.company" /> {company?.name}
                    </span>
                    <Button size={"lg"} type={"submit"} color={"primary"} onPress={form.submit} isLoading={loading}>
                        <FormattedMessage id={"app.button.save"} />
                    </Button>
                </Flex>
            }
        >
            <Spin spinning={loading}>
                <Form onFinish={handleSubmit} layout="vertical" form={form}>
                    <Form.Item
                        label={<FormattedMessage id="app.fields.internalDesignation" />}
                        name="name"
                        rules={[validation.required(intl), validation.max(intl, 100)]}
                    >
                        <Input maxLength={100} />
                    </Form.Item>
                    <Form.Item
                        label={<FormattedMessage id="app.fields.companyNameWithLegalForm" />}
                        name="legalName"
                        rules={[validation.required(intl), validation.max(intl, 60)]}
                    >
                        <Input maxLength={100} />
                    </Form.Item>
                    <Flex justify={"space-between"}>
                        <Form.Item
                            label={<FormattedMessage id="app.fields.legalForm" />}
                            name="legalForm"
                            rules={[validation.required(intl), validation.max(intl, 60)]}
                        >
                            <LegalFormSelect />
                        </Form.Item>
                        {selectOptions.length > 0 && (
                            <Form.Item
                                label={<FormattedMessage id="app.fields.favouritePaymentType" />}
                                name="favouritePaymentType"
                            >
                                <Select
                                    notFoundContent={<FormattedMessage id="app.components.table.no_items" />}
                                    labelRender={props => (
                                        <PaymentTypeOption
                                            paymentType={selectOptions.find(o => o.uuid === props.value)}
                                            year={year}
                                        />
                                    )}
                                >
                                    {selectOptions.map((option, index) => (
                                        <Select.Option key={index} value={option.uuid || option.value}>
                                            <PaymentTypeOption paymentType={option} year={year} />
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                        <Form.Item
                            label={<FormattedMessage id="app.fields.type" />}
                            name="type"
                            style={{ minWidth: 100 }}
                        >
                            <Select
                                notFoundContent={<FormattedMessage id="app.components.table.no_items" />}
                                options={[
                                    {
                                        label: "Gastronomie",
                                        options: [
                                            {
                                                label: "Restaurant",
                                                value: "restaurant",
                                            },
                                            {
                                                label: "Café",
                                                value: "cafe",
                                            },
                                            {
                                                label: "Bäckerei",
                                                value: "bakery",
                                            },
                                        ],
                                    },
                                    {
                                        label: "Handel",
                                        options: [
                                            {
                                                label: "Einzelhandel",
                                                value: "retail",
                                            },
                                            {
                                                label: "Großhandel",
                                                value: "wholesale",
                                            },
                                        ],
                                    },
                                    {
                                        label: "Dienstleistungen",
                                        options: [
                                            {
                                                label: "Beratungsleistungen",
                                                value: "consulting",
                                            },
                                            {
                                                label: "Sonstige Dienstleistungen",
                                                value: "services_other",
                                            },
                                        ],
                                    },
                                ]}
                            />
                        </Form.Item>
                    </Flex>
                    <Flex justify={"space-between"}>
                        <Form.Item label={<FormattedMessage id="app.fields.color" />} name="color">
                            <FormColorPicker />
                        </Form.Item>

                        <Form.Item label={"Bild (Max. 200 kB)"} name="logo">
                            <Flex gap={8}>
                                <Dropzone
                                    accept={{ "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] }}
                                    multiple={false}
                                    maxSize={200000}
                                    onDropAccepted={files => {
                                        form.validateFields(["logo"]);
                                        fileToBase64(files[0]).then(base64 => form.setFieldValue("logo", base64));
                                    }}
                                    onDropRejected={fileRejections => {
                                        const errors: string[] = [];
                                        fileRejections.forEach(file => {
                                            file.errors.forEach(err => {
                                                errors.push(err.message);
                                                // if (err.code === "file-too-large") {
                                                //     // setErrors(`Error: ${err.message}`);
                                                //
                                                // }
                                            });
                                        });
                                        form.setFields([
                                            {
                                                name: "logo",
                                                errors,
                                            },
                                        ]);
                                    }}
                                >
                                    {({ getRootProps, getInputProps }) => (
                                        <div {...getRootProps()}>
                                            <input {...getInputProps()} />

                                            <Button
                                                style={{ width: 250 }}
                                                variant="ghost"
                                                startContent={<UploadOutlined />}
                                                onPress={e => getRootProps().onClick(e as any)}
                                            >
                                                {intl.formatMessage({ id: "app.button.upload" })}
                                                &nbsp;.JPG, &nbsp;.PNG
                                            </Button>
                                        </div>
                                    )}
                                </Dropzone>
                                <Button
                                    variant={"light"}
                                    isDisabled={!logo}
                                    isIconOnly
                                    onPress={() =>
                                        form.setFields([
                                            {
                                                name: "logo",
                                                errors: [],
                                                value: undefined,
                                            },
                                        ])
                                    }
                                >
                                    <CloseOutlined />
                                </Button>
                            </Flex>
                        </Form.Item>
                    </Flex>

                    <Flex justify={"space-between"}>
                        {hasAccounting ? (
                            <Form.Item label="Accounting years">
                                <pre>{company.accountingYears.toString()}</pre>
                            </Form.Item>
                        ) : (
                            <div>&nbsp;</div>
                        )}
                        <Image
                            preview={false}
                            width={200}
                            src={logo}
                            fallback="data:image/png;base64,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"
                        />
                    </Flex>

                    <Divider orientation="left">DATEV</Divider>
                    <Space direction="horizontal">
                        <Form.Item
                            label={<FormattedMessage id="app.fields.datevNrConsultant" />}
                            name="datevNrConsultant"
                            rules={[validation.onlyNumber, validation.inRange(1001, 9999999)]}
                        >
                            <Input maxLength={7} disabled={isDatevConnected} />
                        </Form.Item>
                        <Form.Item
                            label={<FormattedMessage id="app.fields.datevNrCompany" />}
                            name="datevNrCompany"
                            rules={[validation.onlyNumber, validation.inRange(1, 99999)]}
                        >
                            <Input maxLength={5} disabled={isDatevConnected} />
                        </Form.Item>
                        {!isDatevConnected && !isStepperMode && (
                            <Form.Item
                                label={
                                    datevUserInfo ? (
                                        <>&nbsp;</>
                                    ) : (
                                        <div>
                                            <Link
                                                to={AppRoutes.profile + "?tab=datev"}
                                                style={{ color: "green", textDecoration: "underline" }}
                                            >
                                                DATEV Login
                                            </Link>
                                        </div>
                                    )
                                }
                            >
                                <Tooltip
                                    title={
                                        isDatevConnectionAvailable ? null : (
                                            <Space direction={"vertical"}>
                                                {availableDatevIds.map(id => (
                                                    <Kbd key={id}>{id}</Kbd>
                                                ))}
                                            </Space>
                                        )
                                    }
                                >
                                    <DatevButton
                                        disabled={!isDatevConnectionAvailable}
                                        loading={isDatevLoading}
                                        onClick={handleDatevAuth}
                                    >
                                        DATEV verbinden
                                    </DatevButton>
                                </Tooltip>
                            </Form.Item>
                        )}
                    </Space>
                    {isDatevConnected && (
                        <Accordion variant="shadow">
                            <AccordionItem
                                title={
                                    <strong>
                                        DATEV ist verbunden <CheckCircleTwoTone twoToneColor={"#96d551"} />
                                    </strong>
                                }
                            >
                                <div>
                                    <DatevDescriptions
                                        layout="vertical"
                                        token={company.datevOAuth}
                                        datevUserInfo={datevCompanyUserInfo}
                                    />
                                    <DatevClients datevUserInfo={datevCompanyUserInfo} />
                                    <Flex justify={"space-between"} style={{ marginTop: 8 }}>
                                        <Button
                                            isDisabled={Datev.getRefreshTokenValidity(company?.datevOAuth) === 0}
                                            onPress={handleRefresh}
                                            variant={"ghost"}
                                            startContent={<ReloadOutlined />}
                                        >
                                            Refresh Token
                                        </Button>
                                        <Popconfirm
                                            onConfirm={handleDatevTokenRevocation}
                                            title={<FormattedMessage id="app.confirmation.header" />}
                                            okText={<FormattedMessage id="app.button.confirm" />}
                                            cancelText={<FormattedMessage id="app.button.cancel" />}
                                        >
                                            <Button variant="ghost" color={"danger"}>
                                                DATEV Verbindung löschen
                                            </Button>
                                        </Popconfirm>
                                    </Flex>
                                </div>
                            </AccordionItem>
                        </Accordion>
                    )}
                </Form>
            </Spin>
        </Drawer>
    );
};
